import { useState, useEffect } from 'react';
import { 
    Box,
    Button,
    Typography,
    Checkbox,
    Paper,
    LinearProgress,
    Card,
    CardContent,
    Grid,
    IconButton
} from '@mui/material';
import { Save as SaveIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';

export const TeamsList = ({ teams, showCheckboxes = false, checked = [], teamsDisabled = [], handleCheck = () => {} }) => {
    return (
        <Grid container spacing={2}>
            {teams.teams.map(team => (
                <Grid item xs={12} key={team.id}>
                    <Paper 
                        onClick={() => showCheckboxes && handleCheck(team.id)}
                        sx={{ 
                            p: 2,
                            opacity: teamsDisabled[team.id] ? 0.6 : 1,
                            transition: 'all 0.2s ease-in-out',
                            cursor: showCheckboxes ? (teamsDisabled[team.id] ? 'not-allowed' : 'pointer') : 'default',
                            '&:hover': showCheckboxes ? {
                                transform: teamsDisabled[team.id] ? 'none' : 'translateY(-4px)',
                                boxShadow: teamsDisabled[team.id] ? 1 : 3,
                                backgroundColor: teamsDisabled[team.id] ? 'inherit' : 'rgba(0, 0, 0, 0.04)'
                            } : {},
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                            {showCheckboxes && (
                                <Checkbox
                                    checked={checked[team.id]}
                                    disabled={teamsDisabled[team.id]}
                                    onChange={() => handleCheck(team.id)}
                                    onClick={(e) => e.stopPropagation()}
                                    sx={{ mr: 2 }}
                                />
                            )}
                            <Box>
                                <Typography variant="body1">
                                    {team.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Очки: {team.weight}
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>
            ))}
        </Grid>
    );
};

const Table = ({saveRes, userData, teams, onBack}) => {
    const [checkedList, setCheckedList] = useState(userData);
    const [checked, setChecked] = useState(() => {
        var checked = []
        for (let i = 0; i < teams.teams.length; ++i)
            checked.push(userData.includes(i))
        return checked
    });
    const [points, setPoints] = useState(() => {
        var point = 0
        for (let i = 0; i < userData.length; ++i)
            point += teams.teams[userData[i]].weight
        return point
    });
    const [max_points] = useState(teams.total_points);
    const [max_team_count] = useState(teams.max_team_count);
    const [teamsDisabled, setDisabled] = useState(() => {
        var disabled = []
        for (let i = 0; i < teams.teams.length; ++i) {
            if (userData.includes(i))
                disabled.push(false);
            else
                disabled.push(teams.teams[i].weight + points > max_points || userData.length >= max_team_count);
        }
        return disabled
    });

    // Обновляем состояние при изменении userData
    useEffect(() => {
        setCheckedList(userData);
        const newChecked = [];
        for (let i = 0; i < teams.teams.length; ++i) {
            newChecked.push(userData.includes(i));
        }
        setChecked(newChecked);

        const newPoints = userData.reduce((sum, id) => sum + teams.teams[id].weight, 0);
        setPoints(newPoints);

        const newDisabled = [];
        for (let i = 0; i < teams.teams.length; ++i) {
            if (userData.includes(i))
                newDisabled.push(false);
            else
                newDisabled.push(teams.teams[i].weight + newPoints > max_points || userData.length >= max_team_count);
        }
        setDisabled(newDisabled);
    }, [userData, teams, max_points, max_team_count]);

    const handleCheck = (id) => {
        if (teamsDisabled[id])
            return;
        var updatedCheckedList = [...checkedList];
        var new_points = points;
        var new_checked = [...checked];
        new_checked[id] = !new_checked[id];
        setChecked(new_checked);
        
        if (new_checked[id]) {
            new_points = points + teams.teams[id].weight;
            updatedCheckedList.push(id);
        } else {
            new_points = points - teams.teams[id].weight;
            updatedCheckedList.splice(updatedCheckedList.indexOf(id), 1);
        }
        
        setPoints(new_points);
        setCheckedList(updatedCheckedList);
        
        var updatedDisables = [...teamsDisabled];
        for (let i = 0; i < teams.teams.length; ++i) {
            if (new_checked[i])
                continue;
            updatedDisables[i] = teams.teams[i].weight + new_points > max_points || updatedCheckedList.length >= max_team_count;
        }
        setDisabled(updatedDisables);
    };

    const pointsPercentage = (points / max_points) * 100;

    const handleBackToTournaments = () => {
        // Используем переданную функцию для возврата к списку турниров
        if (onBack) {
            onBack();
        }
    };

    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
            {teams.mode === "Vote" && (
                <Card sx={{ mb: 4 }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, position: 'fixed', top: 0, left: 0, right: 0, backgroundColor: 'white', zIndex: 10, p: 2, boxShadow: 1, borderBottom: '1px solid #ddd' }}>
                            <IconButton
                                color="inherit"
                                onClick={handleBackToTournaments}
                                sx={{ mr: 2 }}
                            >
                                <ArrowBackIcon />
                            </IconButton>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<SaveIcon />}
                                onClick={() => { saveRes(checkedList) }}
                            >
                                Сохранить выбор
                            </Button>
                            <Box>
                                <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                                    Команд выбрано: {checkedList.length} / {max_team_count}
                                </Typography>
                                <Typography variant="subtitle1" color="text.secondary">
                                    Очков использовано: {points} / {max_points}
                                </Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            )}

            <Box sx={{ mt: teams.mode === "Vote" ? 10 : 0 }}>
                {teams.mode === "Vote" ? (
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                        Сохраненный выбор можно изменять до ЧГК
                    </Typography>
                ) : (
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                        Турнир {teams.mode === "Results" ? "завершен" : "закрыт для голосования"}
                    </Typography>
                )}

                <TeamsList 
                    teams={teams}
                    showCheckboxes={teams.mode === "Vote"}
                    checked={checked}
                    teamsDisabled={teamsDisabled}
                    handleCheck={handleCheck}
                />
            </Box>
        </Box>
    );
}

export default Table;